
import { ListRoles, CreateRole, UpdateRole, SetRoleDataScope, AssignPermissionsToRole, DeleteRole } from '@/api/orgAdmin'
import { ListDepts } from '@/api/orgAdmin'
import { ListPermissions } from '@/api/orgAdmin'
import { IAllRoleInfo, IAllDeptInfo, DataScope } from '@/api/types'
import { formatDate } from '@/utils/index'
import { Form as ElForm } from 'element-ui'
import { Component, Vue } from 'vue-property-decorator'

// 定义部门接口
interface IDept {
  id: number
  name: string
}

@Component({
  name: 'OrgAdminRoles'
})
export default class extends Vue {
  public roleList: IAllRoleInfo[] = []
  public listLoading = true
  public deptList: IAllDeptInfo[] = []
  public deptLoading = false
  public permissionList: string[] = []
  public permissionLoading = false
  public selectAll = false

  // 角色对话框相关
  public roleDialogVisible = false
  public dialogStatus = 'create'
  public roleForm = {
    id: 0,
    name: '',
    desc: '',
    status: 1
  }

  public roleRules = {
    name: [
      { required: true, message: '请输入角色名称', trigger: 'blur' },
      { min: 2, max: 50, message: '长度在 2 到 50 个字符', trigger: 'blur' }
    ],
    desc: [
      { max: 200, message: '长度不能超过 200 个字符', trigger: 'blur' }
    ]
  }

  // 权限对话框相关
  public permissionDialogVisible = false
  public currentRoleId = 0
  public checkedPermissions: string[] = []

  // 数据范围对话框相关
  public dataScopeDialogVisible = false
  public dataScopeForm = {
    roleId: 0,
    dataScope: 0,
    deptIds: [] as number[]
  }

  public dataScopeOptions = [
    { label: '全部数据权限', value: DataScope.All },
    { label: '自定义数据权限', value: DataScope.Custom },
    { label: '本部门数据权限', value: DataScope.Dept },
    { label: '本部门及以下数据权限', value: DataScope.DeptAndDown },
    { label: '仅本人数据权限', value: DataScope.Self }
  ]

  created() {
    this.getRoleList()
    this.fetchDepts()
    this.fetchPermissions()
  }

  public async getRoleList() {
    this.listLoading = true
    try {
      const response = await ListRoles()
      if (response && response.data) {
        this.roleList = Array.isArray(response.data) ? response.data : []
      } else {
        this.roleList = []
      }
    } catch (error) {
      console.error('获取角色列表失败:', error)
      this.$message.error('获取角色列表失败')
      this.roleList = []
    } finally {
      this.listLoading = false
    }
  }

  public async fetchDepts() {
    try {
      this.deptLoading = true
      const response = await ListDepts()
      if (response && response.data) {
        this.deptList = Array.isArray(response.data) ? response.data : []
      } else {
        this.deptList = []
      }
    } catch (error) {
      console.error('获取部门列表失败:', error)
      this.deptList = []
    } finally {
      this.deptLoading = false
    }
  }

  public async fetchPermissions() {
    try {
      this.permissionLoading = true
      const response = await ListPermissions()
      if (response && response.data) {
        this.permissionList = Array.isArray(response.data) ? response.data : []
      } else {
        this.permissionList = []
      }
    } catch (error) {
      console.error('获取权限列表失败:', error)
      this.permissionList = []
    } finally {
      this.permissionLoading = false
    }
  }

  public formatTime(time: string) {
    if (!time) return '-'
    return formatDate(time, 'YYYY-MM-DD HH:mm:ss')
  }

  public getDataScopeLabel(dataScope: number): string {
    const option = this.dataScopeOptions.find(item => item.value === dataScope)
    return option ? option.label : '未知范围'
  }

  public handleCreateRole() {
    this.dialogStatus = 'create'
    this.roleForm = {
      id: 0,
      name: '',
      desc: '',
      status: 1
    }
    this.roleDialogVisible = true
    this.$nextTick(() => {
      (this.$refs.roleForm as ElForm).clearValidate()
    })
  }

  public handleEditRole(row: IAllRoleInfo) {
    if (!row || !row.role) {
      this.$message.error('角色数据不完整，无法编辑')
      return
    }

    this.dialogStatus = 'update'
    this.roleForm = {
      id: row.role.id,
      name: row.role.name,
      desc: row.role.desc || '',
      status: 1
    }
    this.roleDialogVisible = true
    this.$nextTick(() => {
      (this.$refs.roleForm as ElForm).clearValidate()
    })
  }

  public async submitRoleForm() {
    (this.$refs.roleForm as ElForm).validate(async(valid: boolean) => {
      if (valid) {
        try {
          if (this.dialogStatus === 'create') {
            await CreateRole({
              name: this.roleForm.name,
              desc: this.roleForm.desc
            })
            this.$message.success('角色创建成功')
          } else {
            await UpdateRole({
              id: this.roleForm.id,
              name: this.roleForm.name,
              desc: this.roleForm.desc
            })
            this.$message.success('角色更新成功')
          }
          this.roleDialogVisible = false
          this.getRoleList()
        } catch (error) {
          console.error('保存角色失败:', error)
          this.$message.error('保存角色失败')
        }
      }
    })
  }

  public handleAssignPermissions(row: IAllRoleInfo) {
    if (!row || !row.role) {
      this.$message.error('角色数据不完整，无法分配权限')
      return
    }

    this.currentRoleId = row.role.id
    // 获取角色已有权限
    this.checkedPermissions = row.permissions || []
    // 检查是否全选
    this.selectAll = this.permissionList.length > 0 &&
                     this.checkedPermissions.length === this.permissionList.length
    this.permissionDialogVisible = true
  }

  public handleSelectAllChange(val: boolean) {
    this.checkedPermissions = val ? [...this.permissionList] : []
  }

  public async savePermissions() {
    try {
      await AssignPermissionsToRole({
        roleId: this.currentRoleId,
        permissions: this.checkedPermissions
      })

      this.$message.success('权限分配成功')
      this.permissionDialogVisible = false
      this.getRoleList()
    } catch (error) {
      console.error('权限分配失败:', error)
      this.$message.error('权限分配失败')
    }
  }

  public handleSetDataScope(row: IAllRoleInfo) {
    if (!row || !row.role) {
      this.$message.error('角色数据不完整，无法设置数据范围')
      return
    }

    this.dataScopeForm = {
      roleId: row.role.id,
      dataScope: row.dataScope || 0,
      deptIds: row.customDepts ? row.customDepts.map(d => d.id) : []
    }

    this.dataScopeDialogVisible = true
  }

  public async saveDataScope() {
    try {
      await SetRoleDataScope({
        roleId: this.dataScopeForm.roleId,
        dataScope: this.dataScopeForm.dataScope,
        deptIds: this.dataScopeForm.dataScope === 1 ? this.dataScopeForm.deptIds : []
      })

      this.$message.success('数据范围设置成功')
      this.dataScopeDialogVisible = false
      this.getRoleList()
    } catch (error) {
      console.error('设置数据范围失败:', error)
      this.$message.error('设置数据范围失败')
    }
  }

  // 计算属性：有效的部门列表
  get validDeptList() {
    return this.deptList
  }

  public getDisplayPermissions(permissions: string[] | undefined): string[] {
    if (!permissions || permissions.length === 0) {
      return []
    }
    // 只显示前3个权限，其余的用数字表示
    return permissions.slice(0, 3)
  }

  public getDeptNames(depts: IDept[]): string {
    if (!depts || depts.length === 0) {
      return ''
    }
    return depts.map(dept => dept.name).join('、')
  }

  public getDisplayDepts(depts: IDept[] | undefined): IDept[] {
    if (!depts || depts.length === 0) {
      return []
    }
    // 只显示前2个部门，其余的用数字表示
    return depts.slice(0, 2)
  }

  public handleDeleteRole(row: IAllRoleInfo) {
    if (!row || !row.role) {
      this.$message.error('角色数据不完整，无法删除')
      return
    }

    this.$confirm('确认删除该角色吗？删除后不可恢复', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async() => {
      try {
        await DeleteRole({ id: row.role.id })
        this.$message.success('角色删除成功')
        this.getRoleList()
      } catch (error) {
        console.error('删除角色失败:', error)
        this.$message.error('删除角色失败')
      }
    }).catch(() => {
      // 用户取消删除操作
    })
  }
}
